import React from 'react';
import { Box, Container, Typography, AppBar, Toolbar, Stack, ThemeProvider, CssBaseline, useTheme } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { inputsCustomizations } from '../../shared-theme/customizations/inputs';
import { dataDisplayCustomizations } from '../../shared-theme/customizations/dataDisplay';
import { feedbackCustomizations } from '../../shared-theme/customizations/feedback';
import { navigationCustomizations } from '../../shared-theme/customizations/navigation';
import { surfacesCustomizations } from '../../shared-theme/customizations/surfaces';
import { colorSchemes, typography, shadows, shape } from '../../shared-theme/themePrimitives';
import EmailIcon from '@mui/icons-material/Email';
import { PodcastIcon } from '@/components/icons/PodcastIcon';

function ContactContent() {
  const theme = useTheme();

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
       {/* Simple Navbar */}
       <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
            borderBottom: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(12px)',
            backgroundColor: 'rgba(255, 255, 255, 0.8)', 
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ height: 64 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ cursor: 'pointer' }} onClick={() => window.location.href = '/'}>
               <PodcastIcon size={32} color={theme.palette.primary.main} />
               <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: '-0.5px', color: 'text.primary' }}>
                Podstack
              </Typography>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 12, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
         <Stack spacing={4} alignItems="center" textAlign="center">
            <Box sx={{ width: 64, height: 64, bgcolor: 'primary.50', color: 'primary.main', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <EmailIcon fontSize="large" />
            </Box>
            <Box>
                <Typography variant="h3" fontWeight={800} sx={{ mb: 2 }}>Get in touch</Typography>
                <Typography variant="h6" color="text.secondary" fontWeight={400}>
                    Have questions about Podstack? We'd love to hear from you.
                </Typography>
            </Box>

            <Box sx={{ p: 4, border: '1px solid', borderColor: 'divider', borderRadius: 4, width: '100%', bgcolor: 'background.paper', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>Customer Support</Typography>
                <Typography variant="h5" fontWeight={700} color="primary.main" sx={{ mt: 1 }}>
                    <a href="mailto:support@podstack.co" style={{ color: 'inherit', textDecoration: 'none' }}>support@podstack.co</a>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    For general inquiries, billing, or technical support.
                </Typography>
            </Box>

            <Box sx={{ p: 4, border: '1px solid', borderColor: 'divider', borderRadius: 4, width: '100%', bgcolor: 'background.paper', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>Sales & Partnerships</Typography>
                <Typography variant="h5" fontWeight={700} color="primary.main" sx={{ mt: 1 }}>
                    <a href="mailto:sales@podstack.co" style={{ color: 'inherit', textDecoration: 'none' }}>sales@podstack.co</a>
                </Typography>
                 <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    For enterprise plans, api access, and partnership opportunities.
                </Typography>
            </Box>

             <Typography variant="body2" color="text.secondary">
                    We typically respond within 24 hours on business days.
             </Typography>
         </Stack>
      </Container>


       {/* Simple Footer */}
       <Box sx={{ py: 6, bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider', mt: 'auto' }}>
            <Container maxWidth="lg">
                <Typography variant="body2" color="text.disabled" align="center">
                    © {new Date().getFullYear()} Podstack Inc.
                </Typography>
            </Container>
        </Box>

    </Box>
  );
}


export default function ContactPage() {
    const lightTheme = React.useMemo(() => createTheme({
      cssVariables: {
        colorSchemeSelector: 'data-mui-color-scheme',
        cssVarPrefix: 'template',
      },
      colorSchemes, 
      typography,
      shadows,
      shape,
      components: {
        ...inputsCustomizations,
        ...dataDisplayCustomizations,
        ...feedbackCustomizations,
        ...navigationCustomizations,
        ...surfacesCustomizations,
      },
    }), []);
  
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <ContactContent />
      </ThemeProvider>
    );
  }
